// src/components/EditPlaylistModal.js

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

// 🌟 UPGRADED: Accepting the global context `isDarkMode` parameter
const EditPlaylistModal = ({ isOpen, onClose, playlist, isDarkMode }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (playlist) {
      setName(playlist.name || '');
      setDescription(playlist.description || '');
      setIsPrivate(playlist.isPrivate || false);
    }
  }, [playlist]);

  const handleSave = async () => {
    if (!playlist?.id) {
      alert("Error: Playlist data is missing. Cannot save.");
      return;
    }
    
    if (!name.trim()) {
      alert('Playlist name cannot be empty.');
      return;
    }
    setIsLoading(true);
    const db = getFirestore();
    const playlistRef = doc(db, 'playlists', playlist.id);

    try {
      await updateDoc(playlistRef, {
        name: name,
        name_lowercase: name.toLowerCase(),
        description: description,
        isPrivate: isPrivate
      });
      onClose(); // Close modal on success
    } catch (error) {
      console.error("Error updating playlist:", error);
      toast.error("Failed to update playlist.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          {/* 🌟 UPGRADED: Unified Light/Dark Modal Base Chassis */}
          <motion.div
            className={`rounded-2xl shadow-2xl p-6 w-full max-w-sm border transition-all duration-300 ${
              isDarkMode 
                ? "bg-zinc-900 border-zinc-800 text-white shadow-black/60" 
                : "bg-white border-slate-100 text-slate-900 shadow-slate-200"
            }`}
            initial={{ scale: 0.9, y: 10 }} 
            animate={{ scale: 1, y: 0 }} 
            exit={{ scale: 0.9, opacity: 0, y: 10 }}
            transition={{ type: "spring", duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-black uppercase tracking-wider mb-4 italic">
              Edit Playlist
            </h2>
            
            <div className="space-y-4">
              {/* 🌟 UPGRADED: Dynamic Text Input Deck */}
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Playlist Name"
                className={`w-full p-3 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 ${
                  isDarkMode 
                    ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
                    : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                }`}
              />
              
              {/* 🌟 UPGRADED: Dynamic Description Textarea Deck */}
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (optional)"
                rows="3"
                className={`w-full p-3 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 resize-none ${
                  isDarkMode 
                    ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
                    : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                }`}
              />
              {/* 🌟 NEW: Visibility configuration field layout wrapper */}
              <div 
                onClick={() => setIsPrivate(!isPrivate)}
                className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer select-none transition-colors ${
                  isDarkMode ? 'bg-zinc-950 border-zinc-800 hover:bg-zinc-900/60' : 'bg-slate-50 border-slate-200 hover:bg-slate-100/50'
                }`}
              >
                <div>
                  <p className="text-xs font-bold uppercase tracking-tight">Private Playlist</p>
                  <p className={`text-[10px] font-medium mt-0.5 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
                    {isPrivate ? 'Hidden from your public user profile.' : 'Visible to anyone visiting your public page.'}
                  </p>
                </div>
                
                {/* Toggle Slide Knob Pill */}
                <div className={`w-9 h-5 rounded-full p-0.5 transition-colors duration-200 ${isPrivate ? 'bg-brand-pink' : 'bg-zinc-700'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 ${isPrivate ? 'translate-x-4' : 'translate-x-0'}`} />
                </div>
              </div>
            </div>
            
            {/* Action Trigger Row */}
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={onClose} 
                className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-colors focus:outline-none ${
                  isDarkMode 
                    ? "text-zinc-400 hover:text-white hover:bg-zinc-800" 
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                Cancel
              </button>
              
              {/* 🌟 UPGRADED: Atomic Brand UI Payout Save Trigger Button */}
              <button 
                onClick={handleSave} 
                disabled={isLoading} 
                className={`px-5 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all shadow-md active:scale-95 disabled:pointer-events-none ${
                  isLoading
                    ? "bg-zinc-700 text-zinc-500"
                    : isDarkMode
                      ? "bg-brand-pink text-white hover:brightness-110"
                      : "bg-pink-600 text-white hover:bg-pink-700"
                }`}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditPlaylistModal;